import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';

/** Semantic status tokens for the progress bar fill. */
export type ProgressBarStatus = 'on-track' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Size variants controlling track height. */
export type ProgressBarSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * Determinate data progress bar. Use for "7 of 10 goals met" style ratios, not
 * indeterminate loading (use `LoadingBarComponent` for that).
 *
 * Fill color is driven by the `status` input, which maps to brand status tokens.
 * Status is also communicated via `role="progressbar"` ARIA attributes, never by
 * color alone.
 *
 * @example
 * <app-progress-bar [value]="7" [max]="10" status="on-track" [showLabel]="true" />
 *
 * @feature shared
 */
@Component({
  selector: 'app-progress-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div
      role="progressbar"
      [attr.aria-valuenow]="value()"
      [attr.aria-valuemin]="0"
      [attr.aria-valuemax]="max()"
      [attr.aria-label]="ariaLabel()"
      class="relative w-full overflow-hidden rounded-full bg-surface-high"
      [class.h-1]="size() === 'xs'"
      [class.h-2]="size() === 'sm'"
      [class.h-3]="size() === 'md'"
      [class.h-4]="size() === 'lg'"
    >
      <div
        class="h-full transition-all duration-300 ease-out"
        [class.rounded-full]="true"
        [class]="fillClass()"
        [style.width.%]="percent()"
      ></div>
    </div>

    @if (showLabel()) {
      <div class="mt-1 flex items-center justify-between text-xs text-muted">
        <span>{{ labelLeft() ?? value() + ' of ' + max() }}</span>
        <span>{{ labelRight() ?? (percent() | number: '1.0-0') + '%' }}</span>
      </div>
    }
  `,
  imports: [DecimalPipe],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ProgressBarComponent {
  /** Current value. @default 0 */
  readonly value = input<number>(0);

  /** Maximum value. @default 100 */
  readonly max = input<number>(100);

  /** Status token controlling the fill color. @default 'on-track' */
  readonly status = input<ProgressBarStatus>('on-track');

  /** Track size variant. @default 'sm' */
  readonly size = input<ProgressBarSize>('sm');

  /** Show the value/percent label below the bar. @default false */
  readonly showLabel = input<boolean>(false);

  /** Custom left label (defaults to "value of max"). */
  readonly labelLeft = input<string | undefined>(undefined);

  /** Custom right label (defaults to "percent%"). */
  readonly labelRight = input<string | undefined>(undefined);

  /** Accessible label for the progress bar. */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Clamped percent from 0–100. */
  readonly percent = computed(() => {
    const max = Math.max(1, this.max());
    const raw = (this.value() / max) * 100;
    return Math.max(0, Math.min(100, raw));
  });

  /** Tailwind background class for the fill, driven by `status`. */
  readonly fillClass = computed(() => {
    switch (this.status()) {
      case 'on-track':
        return 'bg-primary';
      case 'success':
        return 'bg-success';
      case 'warning':
        return 'bg-warning';
      case 'danger':
        return 'bg-danger';
      case 'info':
        return 'bg-blue';
      case 'neutral':
      default:
        return 'bg-medium';
    }
  });
}
